import { ColumnDef, Table } from "@tanstack/react-table";
import { useMemo } from "react";

import { ContentContainer, PageLayout, PageTitle } from "@/components/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetPriceRemarkJustificationsQuery } from "@/services/price-remark/monitoring";
import {
  JustificationExceptionRow,
  JustificationSummaryRow,
} from "@/services/price-remark/types";

import {
  CompositionDonut,
  NamedValue,
  RankingBarChart,
} from "../../components/charts";
import { PriceRemarkFilter } from "../../components/filter";
import { KpiCards } from "../../components/kpi-cards";
import { QueryErrorAlert } from "../../components/query-error-alert";
import { ColumnHeader } from "../../components/table/column-header";
import { MonitoringTable } from "../../components/table/monitoring-table";
import { usePriceRemarkFilterState } from "../../ducks/filter/hooks/filterState";
import { formatLojaLabel } from "../../utils/format-loja";
import { extractQueryErrorMessage } from "../../utils/query-error";

function sumFooter<T>(id: string) {
  return ({ table }: { table: Table<T> }) =>
    table
      .getFilteredRowModel()
      .rows.reduce((acc, row) => acc + Number(row.getValue(id) || 0), 0);
}

const summaryColumns: ColumnDef<JustificationSummaryRow>[] = [
  {
    accessorKey: "loja",
    header: ({ column }) => <ColumnHeader column={column} title="Loja" />,
    meta: { label: "Loja" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatLojaLabel(row.original)}
      </span>
    ),
  },
  {
    accessorKey: "motivo",
    header: ({ column }) => <ColumnHeader column={column} title="Motivo" />,
    meta: { label: "Motivo" },
  },
  {
    accessorKey: "ocorrencias",
    header: ({ column }) => <ColumnHeader column={column} title="Ocorrências" />,
    meta: { label: "Ocorrências" },
    footer: sumFooter<JustificationSummaryRow>("ocorrencias"),
  },
  {
    accessorKey: "soma_divergencia",
    header: ({ column }) => <ColumnHeader column={column} title="Divergência" />,
    meta: { label: "Divergência" },
    cell: ({ row }) => (
      <span
        className={row.original.soma_divergencia < 0 ? "text-red-600" : ""}
      >
        {row.original.soma_divergencia}
      </span>
    ),
    footer: sumFooter<JustificationSummaryRow>("soma_divergencia"),
  },
  {
    accessorKey: "skus",
    header: ({ column }) => <ColumnHeader column={column} title="SKUs" />,
    meta: { label: "SKUs" },
  },
  {
    accessorKey: "operadores",
    header: ({ column }) => <ColumnHeader column={column} title="Operadores" />,
    meta: { label: "Operadores" },
  },
];

const exceptionColumns: ColumnDef<JustificationExceptionRow>[] = [
  { accessorKey: "data", header: "Data", meta: { label: "Data" } },
  {
    accessorKey: "loja",
    header: "Loja",
    meta: { label: "Loja" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatLojaLabel(row.original)}
      </span>
    ),
  },
  { accessorKey: "contexto", header: "Contexto", meta: { label: "Contexto" } },
  { accessorKey: "sku", header: "SKU", meta: { label: "SKU" } },
  { accessorKey: "operador", header: "Operador", meta: { label: "Operador" } },
  { accessorKey: "texto", header: "Texto livre", meta: { label: "Texto livre" } },
];

export function PriceRemarkJustifications() {
  const { filters } = usePriceRemarkFilterState();
  const { data, isFetching, isError, error, refetch } =
    useGetPriceRemarkJustificationsQuery({
      dtIni: filters.dtIni,
      dtFim: filters.dtFim,
      loja: filters.loja,
      tipo: filters.tipo,
    });

  const resumo = useMemo(() => data?.resumo ?? [], [data]);
  const excecoes = useMemo(() => data?.excecoes ?? [], [data]);
  const conferencia = useMemo(
    () => resumo.filter((row) => row.contexto === "CNF"),
    [resumo],
  );

  const kpis = useMemo(() => {
    const ocorrencias = resumo.reduce((acc, r) => acc + Number(r.ocorrencias || 0), 0);
    const divergencia = conferencia.reduce(
      (acc, r) => acc + Number(r.soma_divergencia || 0),
      0,
    );
    const lojas = new Set(resumo.map((r) => r.loja)).size;
    return [
      { label: "Justificativas", value: ocorrencias },
      {
        label: "Divergência (CNF)",
        value: divergencia,
        tone: (divergencia < 0 ? "danger" : "default") as
          | "danger"
          | "default",
      },
      { label: "Exceções", value: excecoes.length },
      { label: "Lojas", value: lojas },
      { label: "Motivos", value: new Set(conferencia.map((r) => r.motivo)).size },
    ];
  }, [resumo, conferencia, excecoes]);

  const donutData = useMemo<NamedValue[]>(() => {
    const byContext = new Map<string, number>();
    resumo.forEach((r) =>
      byContext.set(r.contexto, (byContext.get(r.contexto) ?? 0) + Number(r.ocorrencias || 0)),
    );
    return [...byContext.entries()].map(([name, value]) => ({ name, value }));
  }, [resumo]);

  const rankingLojas = useMemo<NamedValue[]>(() => {
    const byStore = new Map<string, number>();
    conferencia.forEach((r) => {
      const key = formatLojaLabel(r);
      byStore.set(key, (byStore.get(key) ?? 0) + Math.abs(Number(r.soma_divergencia || 0)));
    });
    return [...byStore.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [conferencia]);

  return (
    <PageLayout>
      <PageTitle
        title="Justificativas e divergência"
        description="Conferência (CNF) por motivo/divergência e exceções em texto livre."
      />
      <ContentContainer>
        <div className="space-y-4">
          <PriceRemarkFilter showTipo />

          {isError ? (
            <QueryErrorAlert
              message={extractQueryErrorMessage(error)}
              onRetry={refetch}
            />
          ) : (
            <>
              <KpiCards items={kpis} />

              {resumo.length > 0 && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <CompositionDonut
                    title="Justificativas por contexto"
                    data={donutData}
                  />
                  <RankingBarChart
                    title="Lojas por divergência acumulada (CNF)"
                    data={rankingLojas}
                    color="#DC2626"
                  />
                </div>
              )}

              <Tabs defaultValue="conferencia" className="w-full">
                <TabsList>
                  <TabsTrigger value="conferencia">Conferência (CNF)</TabsTrigger>
                  <TabsTrigger value="excecoes">
                    Exceções (IMP/REI/SME)
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="conferencia" className="pt-4">
                  <MonitoringTable
                    columns={summaryColumns}
                    data={conferencia}
                    isLoading={isFetching}
                    searchPlaceholder="Buscar loja/motivo..."
                    emptyMessage="Sem conferências no período."
                  />
                </TabsContent>

                <TabsContent value="excecoes" className="pt-4">
                  <MonitoringTable
                    columns={exceptionColumns}
                    data={excecoes}
                    isLoading={isFetching}
                    searchPlaceholder="Buscar texto/operador/SKU..."
                    emptyMessage="Sem exceções no período."
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
}

export default PriceRemarkJustifications;
